import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "../../../auth.service";
import { SingleChoice } from "../../../model/singlechoice";
import {
    RecurrenceChange,
    RecurrenceImpact,
    RecurrencePattern,
} from "../../../model/recurrence";
import { RecurrenceService } from "../../../services/recurrence.service";
import { TicketService } from "../../../services/ticket.service";
import { NavigationHelper } from "../../../services/navigation.helper";

/**
 * Asks what should happen to the tickets already planned when the rule of a
 * running series changes.
 *
 * Slides in beside the ticket, the way every other choice in this app does,
 * rather than covering it with a dialog.
 *
 * It works from the url alone: the ticket id comes from the url and the rule
 * being proposed from the form state the edit screen persisted before sending
 * the user here. That matters on a phone, where the edit screen is destroyed
 * while this panel is open - nothing here may depend on it still being alive.
 * The answer travels back in the url for the same reason.
 */
@Component({
    selector: "recurrence-change-panel",
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: "./recurrence-change-panel.component.html",
    styleUrls: ["./recurrence-change-panel.component.css"],
})
export class RecurrenceChangePanelComponent implements OnInit {
    /** The choice object the frame handed us, so it can tidy up afterwards. */
    @Input() choiceEvent: SingleChoice = SingleChoice.EMPTY;

    impact = new BehaviorSubject<RecurrenceImpact | null>(null);
    loading = true;

    choice: RecurrenceChange = "AFTER_WORKED";

    /**
     * How long the series is, for the "occurrence 2 of 20" on each row.
     *
     * It comes from the series as it stands, not from the rule being proposed:
     * the rows are tickets that already exist, and their place in the series is
     * the one they already have. A series with no end has no total to show, so
     * the row says the occurrence and stops there.
     */
    seriesTotal = 0;
    seriesUnbounded = true;

    private ticketId = 0;
    private rule: RecurrencePattern | null = null;

    constructor(
        private router: Router,
        private nav: NavigationHelper,
        private auth: AuthService,
        private recurrences: RecurrenceService,
        private tickets: TicketService
    ) {}

    ngOnInit(): void {
        // The id comes from the navigation helper, not from ActivatedRoute.
        // This app has a single router-outlet, at the very top; everything
        // below the main screen is placed by *ngIf in the frame templates, so
        // no component down here is a routed component and none of them ever
        // sees a route parameter. The url is read by hand instead.
        this.ticketId = Number(this.nav.freshNavigation.id);

        const state = this.tickets.restoreFormState(false) as any;
        this.rule = state?.recurrence
            ? RecurrencePattern.from(state.recurrence)
            : null;

        if (!this.ticketId || !this.rule) {
            // nothing to ask about; go back rather than show an empty panel
            this.leave(null);
            return;
        }
        this.recurrences
            .byTicket(this.auth.token, this.ticketId)
            .subscribe(() => {
                const view = this.recurrences.currentRecurrence.value;

                this.seriesTotal = view ? view.total : 0;
                this.seriesUnbounded = !view || view.unbounded;

                this.recurrences
                    .impactOfChange(this.auth.token, this.ticketId, this.rule!)
                    .subscribe(() => {
                        this.impact.next(this.recurrences.currentImpact.value);
                        this.loading = false;
                    });
            });
    }

    get count(): number {
        return this.impact.value ? this.impact.value.future.length : 0;
    }

    get touched(): number {
        return this.impact.value
            ? this.impact.value.future.filter((one) => one.hasActivity).length
            : 0;
    }

    get resumeAfterSequence(): number {
        return this.impact.value?.resumeAfterSequence ?? 0;
    }

    get resumeAfterDate(): string | null {
        return this.impact.value?.resumeAfterDate ?? null;
    }

    get unbounded(): boolean {
        return !this.rule || this.rule.endType !== 2;
    }

    get total(): number {
        return this.rule ? this.rule.endCount : 0;
    }

    isCurrent(ticketId: number): boolean {
        return ticketId === this.ticketId;
    }

    confirm(): void {
        this.leave(this.choice);
    }

    abort(): void {
        this.leave(null);
    }

    /**
     * Hands the answer back through the url. A query parameter rather than a
     * callback, because the screen that asked the question may no longer exist.
     */
    private leave(answer: RecurrenceChange | null): void {
        if (this.choiceEvent && this.choiceEvent !== SingleChoice.EMPTY) {
            try {
                this.choiceEvent.choose(this.ticketId, "", "", "", 1);
            } catch {
                // the frame arms this; if it did not, there is nothing to tidy
            }
        }
        this.router.navigate([`tickets/${this.ticketId}/edit`], {
            queryParams: answer ? { onChange: answer } : {},
        });
    }
}
